/**
 * Product Bundle Addons - Enhanced Variant Handling
 *
 * Handles:
 * - Variant selection from variant picker
 * - Updating checkbox value when variant changes
 * - Updating product image and price
 * - Quick view modal integration
 */

if (!customElements.get('product-bundle-addons')) {
  customElements.define(
    'product-bundle-addons',
    class ProductBundleAddons extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        // Wait a tick for children to be fully added
        setTimeout(() => {
          this.init();
        }, 0);
      }

      init() {
        this.variantPickers = this.querySelectorAll('[data-addon-variant-picker]');

        // Initialize each variant picker
        this.variantPickers.forEach(picker => {
          this.initVariantPicker(picker);
        });

        // Handle quick view clicks if enabled
        const quickViewLinks = this.querySelectorAll('[data-quick-view="true"]');
        quickViewLinks.forEach(link => {
          link.addEventListener('click', this.handleQuickView.bind(this));
        });
      }

      initVariantPicker(picker) {
        const productId = picker.dataset.productId;
        const variantData = JSON.parse(picker.querySelector('[data-variant-data]').textContent);
        const addonItem = picker.closest('[data-addon-product-id]');
        const checkbox = addonItem.querySelector('[data-addon-checkbox]');
        const variantTitle = addonItem.querySelector('[data-variant-title]');
        const imageContainer = addonItem.querySelector('.horizontal-product__media');
        const image = imageContainer ? imageContainer.querySelector('img') : null;
        const priceContainer = addonItem.querySelector('.horizontal-product__quantity');
        const variantSelect = picker.querySelector('[data-variant-select]');

        // Add change handler to variant select dropdown
        if (variantSelect) {
          variantSelect.addEventListener('change', (e) => {
            const selectedVariantId = parseInt(e.target.value);

            // Find the selected variant
            const selectedVariant = variantData.variants.find(v => v.id === selectedVariantId);

            if (selectedVariant) {
              // Update checkbox value
              checkbox.value = selectedVariant.id;
              checkbox.dataset.price = selectedVariant.price;

              // Update property inputs with new variant ID
              const propertyInputs = addonItem.querySelectorAll('.addon-property');
              propertyInputs.forEach(input => {
                input.setAttribute('data-variant-id', selectedVariant.id);
              });

              // Update availability
              if (!selectedVariant.available) {
                checkbox.disabled = true;
                checkbox.checked = false;
              } else {
                checkbox.disabled = false;
              }

              // Update variant title
              if (variantTitle) {
                variantTitle.textContent = selectedVariant.title;
              }

              // Update image if variant has one
              if (selectedVariant.featured_image.src && imageContainer) {
                // Create new img element with proper srcset for responsive images
                const baseUrl = selectedVariant.featured_image.src;
                const newImg = document.createElement('img');
                newImg.src = baseUrl;
                newImg.alt = selectedVariant.featured_image.alt || '';
                newImg.loading = 'eager'; // Load immediately for smooth transition

                // Create srcset with multiple widths (same as original)
                const widths = [180, 360, 540];
                const srcsetParts = widths.map(width => {
                  // Remove existing width parameter and add new one
                  const urlWithWidth = baseUrl.replace(/width=\d+/, `width=${width}`);
                  return `${urlWithWidth} ${width}w`;
                });
                newImg.srcset = srcsetParts.join(', ');

                // Copy classes from original image
                if (image) {
                  newImg.className = image.className;
                }

                // Add fade-in transition
                newImg.style.opacity = '0';
                newImg.style.transition = 'opacity 0.2s ease-in-out';

                // Wait for image to load before showing
                newImg.onload = () => {
                  newImg.style.opacity = '1';
                };

                // Replace the entire image container content
                imageContainer.innerHTML = '';
                imageContainer.appendChild(newImg);
              }

              // Update price
              this.updatePrice(priceContainer, selectedVariant);

              // Trigger bundle change event
              this.dispatchBundleChangeEvent();
            }
          });
        }
      }

      getSelectedOptions(picker) {
        const selectedButtons = picker.querySelectorAll('.variant-picker__value.selected');
        const options = [];

        selectedButtons.forEach(button => {
          const index = parseInt(button.dataset.optionIndex);
          options[index] = button.dataset.optionValue;
        });

        return options;
      }

      findMatchingVariant(variants, selectedOptions) {
        return variants.find(variant => {
          return variant.options.every((option, index) => {
            return option === selectedOptions[index];
          });
        });
      }

      updatePrice(priceContainer, variant) {
        // Find the actual price span (not the parent div)
        const priceSpan = priceContainer.querySelector('.price__regular') ||
                         priceContainer.querySelector('.price-item--regular') ||
                         priceContainer.querySelector('[data-price-regular]');

        if (priceSpan) {
          // Format the price
          let formattedPrice;
          if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
            formattedPrice = theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat);
          } else if (window.Shopify && window.Shopify.formatMoney) {
            formattedPrice = Shopify.formatMoney(variant.price);
          } else {
            // Fallback formatting
            formattedPrice = '$' + (variant.price / 100).toFixed(2);
          }

          // Update just the text content of the span (preserves classes)
          priceSpan.textContent = formattedPrice;
        }

        // Update availability text if sold out
        const availabilityText = priceContainer.querySelector('.text-sm-base');
        if (availabilityText) {
          if (!variant.available) {
            availabilityText.textContent = (window.theme && window.theme.strings && window.theme.strings.soldOut) || 'Sold out';
            availabilityText.classList.add('text-opacity');
          } else {
            availabilityText.textContent = '';
          }
        }
      }

      handleQuickView(e) {
        const productHandle = e.currentTarget.dataset.productHandle;
        if (productHandle && window.theme && window.theme.QuickView) {
          e.preventDefault();
          theme.QuickView.show(productHandle);
        }
      }

      dispatchBundleChangeEvent() {
        // Trigger the existing bundle change event for price updates
        const form = document.querySelector(`[form="${this.getAttribute('form')}"]`);
        if (form) {
          form.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  );
}

/**
 * Auto-initialize addon variant pickers when product bundle details opens
 */

// Function to initialize bundle addons
function initializeBundleAddons(detailsElement) {
  const addonsContainer = detailsElement.querySelector('.details__content');
  if (addonsContainer && !addonsContainer.dataset.initialized) {
    // Wrap in custom element for enhanced functionality
    if (!addonsContainer.closest('product-bundle-addons')) {
      const wrapper = document.createElement('product-bundle-addons');
      wrapper.setAttribute('form', detailsElement.getAttribute('form'));

      // Move content into wrapper
      while (addonsContainer.firstChild) {
        wrapper.appendChild(addonsContainer.firstChild);
      }
      addonsContainer.appendChild(wrapper);
    }

    addonsContainer.dataset.initialized = 'true';
  }
}

// Function to set up all bundle details on the page
function setupBundleDetails() {
  const bundleDetails = document.querySelectorAll('[is="product-bundle-details"]');

  bundleDetails.forEach(details => {
    // Check if already open on page load
    if (details.open) {
      initializeBundleAddons(details);
    }

    // Add toggle listener if not already added
    if (!details.dataset.toggleListenerAdded) {
      details.addEventListener('toggle', function() {
        if (this.open) {
          initializeBundleAddons(this);
        }
      });
      details.dataset.toggleListenerAdded = 'true';
    }
  });
}

/**
 * Apply currency conversion for fixed amount discounts
 * Uses window.Shopify.currency.rate to convert from shop currency to presentment currency
 */
function applyDiscountCurrencyConversion() {
  const currencyRate = window.Shopify?.currency?.rate ? Number(window.Shopify.currency.rate) : 1;

  // Skip if no conversion needed (rate is 1 or not available)
  if (currencyRate === 1) return;

  const discountContainers = document.querySelectorAll('[data-addon-discount][data-discount-type="fixed_amount"]');

  discountContainers.forEach(container => {
    const discountValue = parseFloat(container.dataset.discountValue) || 0; // Shop currency dollars
    const originalPriceCents = parseFloat(container.dataset.originalPrice) || 0; // Shop currency cents

    if (discountValue <= 0) return;

    // Convert everything to presentment currency
    // originalPriceCents is in shop currency cents (e.g., 2000 USD cents = $20)
    // discountValue is in shop currency dollars (e.g., 10 USD = $10)
    const originalPriceConverted = Math.round(originalPriceCents * currencyRate); // Presentment cents
    const discountValueConverted = discountValue * currencyRate; // Presentment dollars
    const discountCentsConverted = Math.round(discountValueConverted * 100); // Presentment cents

    // Calculate discounted price in presentment currency
    const discountedPrice = Math.max(0, originalPriceConverted - discountCentsConverted);

    // Update the discounted price display
    const priceSpan = container.querySelector('[data-discounted-price]');
    if (priceSpan) {
      let formattedPrice;
      if (window.theme?.Currency?.formatMoney) {
        formattedPrice = theme.Currency.formatMoney(discountedPrice, theme.settings.moneyFormat);
      } else if (window.Shopify?.formatMoney) {
        formattedPrice = Shopify.formatMoney(discountedPrice);
      } else {
        formattedPrice = (discountedPrice / 100).toFixed(2);
      }
      priceSpan.textContent = formattedPrice;
    }

    // Update the badge with converted discount amount
    const discountAmountSpan = container.querySelector('[data-discount-amount]');
    if (discountAmountSpan) {
      let formattedDiscount;
      if (window.theme?.Currency?.formatMoney) {
        formattedDiscount = theme.Currency.formatMoney(discountCentsConverted, theme.settings.moneyFormat);
      } else if (window.Shopify?.formatMoney) {
        formattedDiscount = Shopify.formatMoney(discountCentsConverted);
      } else {
        formattedDiscount = discountValueConverted.toFixed(2);
      }
      discountAmountSpan.textContent = formattedDiscount;
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  setupBundleDetails();

  // Apply currency conversion for fixed amount discounts
  applyDiscountCurrencyConversion();

  // Use event delegation on document for variant select changes
  // This survives DOM replacements from theme variant changes
  document.addEventListener('change', (e) => {
    const variantSelect = e.target.closest('[data-variant-select]');
    if (!variantSelect) return;
    const productId = variantSelect.dataset.productId;
    const addonItem = variantSelect.closest('[data-addon-product-id]');
    if (!addonItem) return;

    const picker = variantSelect.closest('[data-addon-variant-picker]');
    const variantData = JSON.parse(picker.querySelector('[data-variant-data]').textContent);
    const selectedVariantId = parseInt(variantSelect.value);
    const selectedVariant = variantData.variants.find(v => v.id === selectedVariantId);

    if (selectedVariant) {
      const checkbox = addonItem.querySelector('[data-addon-checkbox]');
      const imageContainer = addonItem.querySelector('.horizontal-product__media');
      const image = imageContainer?.querySelector('img');
      const priceContainer = addonItem.querySelector('.horizontal-product__quantity');

      // Update checkbox
      checkbox.value = selectedVariant.id;
      checkbox.dataset.price = selectedVariant.price;
      checkbox.disabled = !selectedVariant.available;
      if (!selectedVariant.available) checkbox.checked = false;

      // Update property inputs with new variant ID
      const propertyInputs = addonItem.querySelectorAll('.addon-property');
      propertyInputs.forEach(input => {
        input.setAttribute('data-variant-id', selectedVariant.id);
      });

      // Update image
      if (selectedVariant.featured_image.src && imageContainer) {
        const baseUrl = selectedVariant.featured_image.src;
        const newImg = document.createElement('img');
        newImg.src = baseUrl;
        newImg.alt = selectedVariant.featured_image.alt || '';
        newImg.loading = 'eager';

        const widths = [180, 360, 540];
        const srcsetParts = widths.map(width => {
          const urlWithWidth = baseUrl.replace(/width=\d+/, `width=${width}`);
          return `${urlWithWidth} ${width}w`;
        });
        newImg.srcset = srcsetParts.join(', ');
        if (image) newImg.className = image.className;

        newImg.style.opacity = '0';
        newImg.style.transition = 'opacity 0.2s ease-in-out';
        newImg.onload = () => { newImg.style.opacity = '1'; };

        imageContainer.innerHTML = '';
        imageContainer.appendChild(newImg);
      }

      // Update price
      const priceSpan = priceContainer.querySelector('.price__regular');
      if (priceSpan) {
        let formattedPrice;
        if (window.theme?.Currency?.formatMoney) {
          formattedPrice = theme.Currency.formatMoney(selectedVariant.price, theme.settings.moneyFormat);
        } else if (window.Shopify?.formatMoney) {
          formattedPrice = Shopify.formatMoney(selectedVariant.price);
        } else {
          formattedPrice = '$' + (selectedVariant.price / 100).toFixed(2);
        }
        priceSpan.textContent = formattedPrice;
      }
    }
  });

  // Use event delegation for quick view clicks
  // This survives DOM replacements from theme variant changes
  document.addEventListener('click', (e) => {
    const quickViewLink = e.target.closest('[data-quick-view="true"]');
    if (!quickViewLink) return;

    const productHandle = quickViewLink.dataset.productHandle;
    const productUrl = quickViewLink.href || `/products/${productHandle}`;

    // Find an existing quick-view element (from product cards/carousels)
    const quickView = document.querySelector('quick-view');

    // If no quick-view exists on the page, fallback to normal navigation
    // This happens on minimal product pages without carousels/product cards
    if (!quickView) {
      console.warn('No quick-view element found on page. Falling back to normal navigation.');
      return; // Allow default link behavior
    }

    // Prevent default and use quick view
    e.preventDefault();

    // Update the product URL and show the modal
    quickView.setAttribute('data-product-url', productUrl);
    quickView.show();
  });
});

// Re-initialize when Shopify section is reloaded (e.g., variant change)
document.addEventListener('shopify:section:load', (event) => {
  setupBundleDetails();
  applyDiscountCurrencyConversion();
});

// Also listen for variant changes on the main product
document.addEventListener('variant:change', () => {
  // Reset initialization flag so it can be re-initialized
  document.querySelectorAll('.details__content[data-initialized]').forEach(el => {
    delete el.dataset.initialized;
  });
  setupBundleDetails();
  applyDiscountCurrencyConversion();
});

// Also run on page:loaded (theme-specific event for dynamic content)
document.addEventListener('page:loaded', () => {
  applyDiscountCurrencyConversion();
});
